import React, { useState, useEffect } from 'react'
import { 
  Card, 
  Typography, 
  List, 
  Tag, 
  Button, 
  Input, 
  Select, 
  Row, 
  Col, 
  Spin, 
  Alert,
  Empty,
  Space,
  Badge
} from 'antd'
import { 
  SearchOutlined, 
  CalendarOutlined, 
  UserOutlined, 
  CarOutlined,
  DollarOutlined,
  EyeOutlined 
} from '@ant-design/icons'
import { useNavigate } from 'react-router-dom'
import { customerApi } from '../services/customerApi'
import dayjs from 'dayjs'

const { Title } = Typography
const { Option } = Select

interface Job {
  id: string
  jobNumber: string
  title: string
  description: string
  status: string
  priority: string
  scheduledDate: string
  serviceAddress: string
  serviceCity: string
  serviceState: string
  serviceZip: string
  assignedTechnician?: {
    id: string
    firstName: string
    lastName: string
    phone: string
  }
  vehicle?: {
    id: string
    name: string
    make: string
    model: string
  }
  estimate?: {
    id: string
    status: string
    option1Total: number
  }
  invoices: Array<{
    id: string
    status: string
    totalAmount: number
  }>
}

const getStatusColor = (status: string) => {
  const colors = {
    'DRAFT': 'default',
    'SCHEDULED': 'blue',
    'IN_PROGRESS': 'orange',
    'COMPLETED': 'green',
    'CANCELLED': 'red',
    'ON_HOLD': 'yellow'
  }
  return colors[status] || 'default'
}

const getPriorityColor = (priority: string) => {
  const colors = {
    'LOW': 'green',
    'NORMAL': 'blue',
    'HIGH': 'orange',
    'URGENT': 'red'
  }
  return colors[priority] || 'default'
}

export const JobList: React.FC = () => {
  const navigate = useNavigate()
  const [jobs, setJobs] = useState<Job[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [searchTerm, setSearchTerm] = useState('')
  const [statusFilter, setStatusFilter] = useState<string>('all')
  const [filteredJobs, setFilteredJobs] = useState<Job[]>([])

  useEffect(() => {
    loadJobs()
  }, [])

  useEffect(() => {
    filterJobs()
  }, [jobs, searchTerm, statusFilter])

  const loadJobs = async () => {
    try {
      setLoading(true)
      setError(null)
      const response = await customerApi.getJobs()
      setJobs(response.jobs || [])
    } catch (err: any) {
      console.error('Failed to load jobs:', err)
      setError(err.response?.data?.message || 'Failed to load jobs')
    } finally {
      setLoading(false)
    }
  }

  const filterJobs = () => {
    let filtered = jobs

    // Filter by status
    if (statusFilter !== 'all') {
      filtered = filtered.filter(job => job.status === statusFilter)
    }

    // Filter by search term
    if (searchTerm) {
      const term = searchTerm.toLowerCase()
      filtered = filtered.filter(job => 
        job.title.toLowerCase().includes(term) ||
        job.jobNumber.toLowerCase().includes(term) ||
        job.description.toLowerCase().includes(term) ||
        job.serviceAddress.toLowerCase().includes(term) ||
        `${job.serviceCity} ${job.serviceState}`.toLowerCase().includes(term)
      )
    }

    setFilteredJobs(filtered)
  }

  const handleViewJob = (jobId: string) => {
    navigate(`/jobs/${jobId}`)
  }

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: '50px' }}>
        <Spin size="large" />
        <p style={{ marginTop: '16px' }}>Loading your jobs...</p>
      </div>
    )
  }

  if (error) {
    return (
      <Alert
        message="Error Loading Jobs"
        description={error}
        type="error"
        showIcon
        action={
          <Button size="small" onClick={loadJobs}>
            Retry
          </Button>
        }
      />
    )
  }

  return (
    <div>
      <Row justify="space-between" align="middle" style={{ marginBottom: '24px' }}>
        <Col>
          <Title level={2}>My Jobs</Title>
        </Col>
        <Col>
          <Badge count={jobs.length} showZero>
            <Button onClick={loadJobs}>Refresh</Button>
          </Badge>
        </Col>
      </Row>

      {/* Filters */}
      <Card style={{ marginBottom: '24px' }}>
        <Row gutter={16}>
          <Col xs={24} sm={12} md={8}>
            <Input
              placeholder="Search jobs..."
              prefix={<SearchOutlined />}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              allowClear
            />
          </Col>
          <Col xs={24} sm={12} md={8}>
            <Select
              style={{ width: '100%' }}
              placeholder="Filter by status"
              value={statusFilter}
              onChange={setStatusFilter}
            >
              <Option value="all">All Statuses</Option>
              <Option value="SCHEDULED">Scheduled</Option>
              <Option value="IN_PROGRESS">In Progress</Option>
              <Option value="COMPLETED">Completed</Option>
              <Option value="ON_HOLD">On Hold</Option>
              <Option value="CANCELLED">Cancelled</Option>
            </Select>
          </Col>
        </Row>
      </Card>

      {/* Job List */}
      {filteredJobs.length === 0 ? (
        <Card>
          <Empty
            description={
              jobs.length === 0 
                ? "No jobs found. Your service requests will appear here."
                : "No jobs match your search criteria."
            }
          />
        </Card>
      ) : (
        <List
          grid={{ gutter: 16, xs: 1, sm: 1, md: 2, lg: 2, xl: 3 }}
          dataSource={filteredJobs}
          renderItem={(job) => (
            <List.Item>
              <Card
                hoverable
                actions={[
                  <Button 
                    type="primary" 
                    icon={<EyeOutlined />}
                    onClick={() => handleViewJob(job.id)}
                  >
                    View Details
                  </Button>
                ]}
              >
                <Card.Meta
                  title={
                    <Space>
                      <span>{job.title}</span>
                      <Tag color={getStatusColor(job.status)}>
                        {job.status.replace('_', ' ')}
                      </Tag>
                    </Space>
                  }
                  description={
                    <div>
                      <p><strong>Job #{job.jobNumber}</strong></p>
                      <p>{job.description}</p>
                      
                      <Space direction="vertical" size="small" style={{ width: '100%' }}>
                        {job.scheduledDate && (
                          <div>
                            <CalendarOutlined /> {dayjs(job.scheduledDate).format('MMM D, YYYY h:mm A')}
                          </div>
                        )}
                        
                        <div>
                          📍 {job.serviceAddress}, {job.serviceCity}, {job.serviceState} {job.serviceZip}
                        </div>

                        {job.assignedTechnician && (
                          <div>
                            <UserOutlined /> {job.assignedTechnician.firstName} {job.assignedTechnician.lastName}
                          </div>
                        )}

                        {job.vehicle && (
                          <div>
                            <CarOutlined /> {job.vehicle.name} ({job.vehicle.make} {job.vehicle.model})
                          </div>
                        )}

                        {job.estimate && (
                          <div>
                            <DollarOutlined /> Estimate: ${Number(job.estimate.option1Total || 0).toFixed(2)}
                          </div>
                        )}

                        <div>
                          <Tag color={getPriorityColor(job.priority)}>
                            {job.priority} Priority
                          </Tag>
                        </div>
                      </Space>
                    </div>
                  }
                />
              </Card>
            </List.Item>
          )}
        />
      )}
    </div>
  )
}
